import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, type PageInfo } from '../api/client';
import type { components } from '../api/schema.gen';
import { BarList } from '../components/BarList';
import { FreeQuotaCard } from '../components/FreeQuotaCard';
import { DateField } from '../components/DateField';
import { EmptyState } from '../components/EmptyState';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { CursorPagePager } from '../components/Pagination';
import { SelectMenu, type SelectMenuOption } from '../components/SelectMenu';
import { initials } from '../lib/avatar';
import { formatDateTime, formatIsoDate } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { ModelVendorMark } from '../lib/modelVendor';
import { formatTHB, formatUsageCost, sum as sumMoney, toMicro } from '../lib/money';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

type Money = components['schemas']['Money'];
type UsageEvent = components['schemas']['UsageEvent'];
type Member = components['schemas']['Member'];
type Bucket = {
  label: string;
  name?: string | null;
  cost: Money;
  requests: number;
  successful_requests: number;
  failed_requests: number;
  input_tokens?: number;
  output_tokens?: number;
  cache_read_tokens?: number;
  cache_write_tokens?: number;
};
type GroupBy = 'day' | 'model' | 'key';
type StatusFilter = 'all' | 'success' | 'failed';

const DEFAULT_DAYS = 7;
const BAR_LIMIT = 12;
const EVENT_PAGE_SIZES = [10, 20, 50] as const;
type EventPageSize = (typeof EVENT_PAGE_SIZES)[number];
const DEFAULT_EVENT_PAGE_SIZE: EventPageSize = 10;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
// The date inputs are calendar days; the contract wants RFC 3339 instants.
// This is a plain UTC-day interpretation, not a timezone-aware one — good
// enough for "show me last week" and clearly documented so nobody mistakes
// it for more precision than it has.
function rangeStart(dateStr: string): string {
  return `${dateStr}T00:00:00Z`;
}
function rangeEnd(dateStr: string): string {
  return `${dateStr}T23:59:59Z`;
}

// b.name is only ever populated for the "key" grouping (see the contract),
// and is null there whenever the key row can't be resolved — the key was
// never named, or the bucket is the "(none)" catch-all for events with no
// key at all.
function keyName(b: Bucket, dict: ReturnType<typeof useLanguage>['dict']): string {
  return b.name && b.name.trim() ? b.name : dict.usage.keyNameNone;
}

// A member's display name if they set one, their email otherwise — the same
// fallback the "สมาชิก" table on the ทีม page already uses.
function memberName(m: { display_name?: string | null; email: string }): string {
  return m.display_name?.trim() ? m.display_name.trim() : m.email;
}

// e.requester is absent for an event with no key_id, or whose key's creator
// has since left the org (LLMR-141) — dict.usage.requesterNone covers both,
// same "(none)" spirit as keyNameNone above.
function requesterName(e: UsageEvent, dict: ReturnType<typeof useLanguage>['dict']): string {
  return e.requester ? memberName(e.requester) : dict.usage.requesterNone;
}

// API statuses stay English; badges must use the same labels as the status
// filter / summary columns (สำเร็จ · ล้มเหลว), not the raw enum string.
function eventStatusLabel(
  status: UsageEvent['status'],
  dict: ReturnType<typeof useLanguage>['dict'],
): string {
  switch (status) {
    case 'failed':
      return dict.usage.statusFailed;
    case 'interrupted':
      return dict.usage.statusInterrupted;
    case 'completed':
    default:
      return dict.usage.statusSuccess;
  }
}

export function Usage() {
  const { ready, hasOrg, canManageBilling } = useSession();
  const { dict } = useLanguage();
  const [onboarding, setOnboarding] = useState(false);

  const groupOptionLabel: Record<GroupBy, string> = {
    day: dict.usage.groupDay,
    model: dict.usage.groupModel,
    key: dict.usage.groupKey,
  };
  const groupAxisLabel: Record<GroupBy, string> = {
    day: dict.usage.groupAxisDay,
    model: dict.usage.groupAxisModel,
    key: dict.usage.groupAxisKey,
  };

  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => isoDate(new Date(today.getTime() - (DEFAULT_DAYS - 1) * 86_400_000)));
  const [to, setTo] = useState(() => isoDate(today));
  const [groupBy, setGroupBy] = useState<GroupBy>('day');
  const [status, setStatus] = useState<StatusFilter>('all');
  // LLMR-141: owner/admin only. '' means "ผู้ใช้งานทั้งหมด" — the page's
  // original, unfiltered behaviour — and is always the default on load.
  const [requesterFilter, setRequesterFilter] = useState('');
  const [members, setMembers] = useState<Member[]>([]);

  const [summary, setSummary] = useState<{ total: Money; buckets: Bucket[] } | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [requesterSummary, setRequesterSummary] = useState<{ total: Money; buckets: Bucket[] } | null>(null);

  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [eventsPage, setEventsPage] = useState<PageInfo | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsLoadedOnce, setEventsLoadedOnce] = useState(false);
  const [eventPageSize, setEventPageSize] = useState<EventPageSize>(DEFAULT_EVENT_PAGE_SIZE);
  // Cursors used to fetch each visited page (index 0 = first page, no cursor).
  // Prev/next walk this stack — cursor lists have no absolute page numbers.
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const eventsSectionRef = useRef<HTMLDivElement | null>(null);
  const scrollEventsAfterLoad = useRef(false);

  function scrollEventsIntoView() {
    const el = eventsSectionRef.current;
    if (!el) return;
    const topbar = document.querySelector('.shell-topbar');
    const offset = (topbar instanceof HTMLElement ? topbar.getBoundingClientRect().height : 72) + 12;
    const top = window.scrollY + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  const loadSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      const res = await api.usageSummary({
        from: rangeStart(from),
        to: rangeEnd(to),
        group_by: groupBy,
        user_id: requesterFilter || undefined,
      });
      setSummary(res);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setOnboarding(true);
      else setSummaryError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.usage.loadErrorSummary);
    }
  }, [from, to, groupBy, requesterFilter, dict]);

  // LLMR-141: "โมเดลที่ [ชื่อ] ใช้" — a second, independent call to the same
  // summary endpoint, always grouped by model, only fetched once a specific
  // requester is picked. No new backend surface.
  useEffect(() => {
    if (!ready || !hasOrg || !canManageBilling || requesterFilter === '') {
      setRequesterSummary(null);
      return;
    }
    let cancelled = false;
    api
      .usageSummary({ from: rangeStart(from), to: rangeEnd(to), group_by: 'model', user_id: requesterFilter })
      .then((res) => {
        if (!cancelled) setRequesterSummary(res);
      })
      .catch(() => {
        if (!cancelled) setRequesterSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, hasOrg, canManageBilling, requesterFilter, from, to]);

  // The requester picker's roster (LLMR-141), fetched once for owner/admin —
  // same call shape RequestTopUpModal already uses for its own org-roster
  // picker. "always a small, bounded catalogue" per GET /team's own contract
  // note, so one page covers every real org.
  useEffect(() => {
    if (!ready || !hasOrg || !canManageBilling) return;
    let cancelled = false;
    api
      .team({ page_size: 200, sort: 'email' })
      .then((res) => {
        if (!cancelled) setMembers(res.data);
      })
      .catch(() => {
        /* the filter just stays empty (only "ทั้งหมด") — not fatal to the page */
      });
    return () => {
      cancelled = true;
    };
  }, [ready, hasOrg, canManageBilling]);

  // The server applies `from`/`to` in SQL as of ADR-0009. Until then it
  // accepted both and ignored them, which is why this table used to show
  // events from outside the range the pickers above it were displaying.
  // Page replace (not append): each response is one screen of rows.
  const loadEvents = useCallback(
    async (cursor?: string) => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const res = await api.usage({
          from: rangeStart(from),
          to: rangeEnd(to),
          limit: eventPageSize,
          cursor,
          status: status === 'all' ? undefined : status,
          user_id: requesterFilter || undefined,
        });
        setEvents(res.data);
        setEventsPage(res.page);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) setOnboarding(true);
        else setEventsError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.usage.loadErrorEvents);
      } finally {
        setEventsLoading(false);
        setEventsLoadedOnce(true);
      }
    },
    [from, to, eventPageSize, status, requesterFilter, dict],
  );

  useEffect(() => {
    if (!ready) return;
    if (!hasOrg) {
      setOnboarding(true);
      return;
    }
    void loadSummary();
  }, [ready, hasOrg, loadSummary]);

  // A date-range or page-size change invalidates the cursor walk as well as
  // the rows: a cursor is a position in one ordered result set.
  useEffect(() => {
    if (!ready || !hasOrg) return;
    setEvents([]);
    setEventsPage(null);
    setEventsLoadedOnce(false);
    setCursorStack([undefined]);
    setPageIndex(0);
    void loadEvents();
  }, [ready, hasOrg, loadEvents]);

  // Scroll after the new page has committed to the DOM — scrolling in the
  // click handler runs before React paints the swapped rows, so it often
  // lands nowhere / stays at the pager.
  useEffect(() => {
    if (eventsLoading || !scrollEventsAfterLoad.current) return;
    scrollEventsAfterLoad.current = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollEventsIntoView();
      });
    });
  }, [eventsLoading, events]);

  function goNext() {
    const next = eventsPage?.next_cursor;
    if (!next) return;
    setCursorStack((prev) => {
      const trimmed = prev.slice(0, pageIndex + 1);
      return [...trimmed, next];
    });
    setPageIndex((i) => i + 1);
    scrollEventsAfterLoad.current = true;
    void loadEvents(next);
  }

  function goPrev() {
    if (pageIndex <= 0) return;
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    scrollEventsAfterLoad.current = true;
    void loadEvents(cursorStack[prevIndex]);
  }

  function changePageSize(size: number) {
    if (!(EVENT_PAGE_SIZES as readonly number[]).includes(size)) return;
    scrollEventsAfterLoad.current = true;
    setEventPageSize(size as EventPageSize);
  }

  // The server still groups by key_id (LLMR-55's owner-scoping and revoked
  // keys both need that precision) — but the UI no longer shows key_id at
  // all, only the resolved name. Left as-is, every key sharing a name (every
  // Playground-auto session mints its own key_id under the same name) would
  // render as unexplained duplicate rows. Re-aggregate client-side by
  // display name instead, so each name is exactly one row/bar.
  const mergedBuckets = useMemo(() => {
    if (!summary) return [];
    if (groupBy !== 'key') return summary.buckets;
    const byName = new Map<string, Bucket>();
    for (const b of summary.buckets) {
      const name = keyName(b, dict);
      const existing = byName.get(name);
      byName.set(
        name,
        existing
          ? {
              ...existing,
              cost: { ...existing.cost, amount: sumMoney([existing.cost.amount, b.cost.amount]) },
              requests: existing.requests + b.requests,
              successful_requests: existing.successful_requests + b.successful_requests,
              failed_requests: existing.failed_requests + b.failed_requests,
              input_tokens: (existing.input_tokens ?? 0) + (b.input_tokens ?? 0),
              output_tokens: (existing.output_tokens ?? 0) + (b.output_tokens ?? 0),
              cache_read_tokens: (existing.cache_read_tokens ?? 0) + (b.cache_read_tokens ?? 0),
              cache_write_tokens: (existing.cache_write_tokens ?? 0) + (b.cache_write_tokens ?? 0),
            }
          : { ...b, label: name },
      );
    }
    return [...byName.values()];
  }, [summary, groupBy, dict]);

  // Chronological for a day-by-day view (the server's own order); ranked by
  // spend for model/key breakdowns, where "which cost the most" is the
  // actual question being asked.
  const orderedBuckets = useMemo(() => {
    if (groupBy === 'day') return mergedBuckets;
    return [...mergedBuckets].sort((a, b) => {
      const diff = toMicro(b.cost.amount) - toMicro(a.cost.amount);
      return diff < 0n ? -1 : diff > 0n ? 1 : 0;
    });
  }, [mergedBuckets, groupBy]);

  // LLMR-141: "ผู้ใช้งานทั้งหมด" pinned first as the default/reset option, then
  // every member — name on top, email as a muted second line only when a
  // name is actually set (otherwise the email alone is already the label,
  // per the card: no redundant second line repeating it).
  const requesterOptions: SelectMenuOption[] = useMemo(
    () => [
      { value: '', label: dict.usage.requesterAll },
      ...members.map((m) => {
        const name = memberName(m);
        return {
          value: m.user_id,
          label: name,
          sublabel: m.display_name?.trim() ? m.email : undefined,
          avatarText: initials(name),
        };
      }),
    ],
    [members, dict],
  );
  const selectedMember = members.find((m) => m.user_id === requesterFilter);

  const requesterModelBuckets = useMemo(() => {
    if (!requesterSummary) return [];
    return [...requesterSummary.buckets].sort((a, b) => {
      const diff = toMicro(b.cost.amount) - toMicro(a.cost.amount);
      return diff < 0n ? -1 : diff > 0n ? 1 : 0;
    });
  }, [requesterSummary]);

  return (
    <div className="page usage-page">
      <PageHeading subtitle={dict.usage.subtitle}>{dict.common.usage}</PageHeading>

      {onboarding ? (
        <OnboardingNotice area={dict.onboarding.usageArea} />
      ) : (
        <>
          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel usage-panel usage-panel--filters">
            <div className="usage-filters">
              <div className="inline-form-field usage-date-field">
                <label htmlFor="usage-from">{dict.usage.fromLabel}</label>
                <DateField id="usage-from" value={from} max={to} rangeEnd={to} onChange={setFrom} aria-label={dict.usage.fromLabel} />
              </div>
              <div className="inline-form-field usage-date-field">
                <label htmlFor="usage-to">{dict.usage.toLabel}</label>
                <DateField
                  id="usage-to"
                  value={to}
                  min={from}
                  max={isoDate(today)}
                  rangeStart={from}
                  onChange={setTo}
                  aria-label={dict.usage.toLabel}
                />
              </div>
              <div className="inline-form-field usage-groupby-field">
                <label htmlFor="usage-group">{dict.usage.groupByLabel}</label>
                <SelectMenu
                  id="usage-group"
                  aria-label={dict.usage.groupByLabel}
                  value={groupBy}
                  onChange={setGroupBy}
                  options={[
                    { value: 'day', label: dict.usage.groupDay },
                    { value: 'model', label: dict.usage.groupModel },
                    { value: 'key', label: dict.usage.groupKey },
                  ]}
                />
              </div>
              <div className="inline-form-field usage-status-field">
                <label htmlFor="usage-status">{dict.usage.statusFilterLabel}</label>
                <SelectMenu
                  id="usage-status"
                  aria-label={dict.usage.statusFilterLabel}
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: 'all', label: dict.usage.statusAll },
                    { value: 'success', label: dict.usage.statusSuccess },
                    { value: 'failed', label: dict.usage.statusFailed },
                  ]}
                />
              </div>
              {canManageBilling && (
                <div className="inline-form-field usage-requester-field">
                  <label htmlFor="usage-requester">{dict.usage.requesterLabel}</label>
                  <SelectMenu
                    id="usage-requester"
                    aria-label={dict.usage.requesterLabel}
                    value={requesterFilter}
                    onChange={setRequesterFilter}
                    searchable
                    searchHint={dict.usage.requesterSearchHint}
                    noResultsLabel={dict.usage.requesterNoResults}
                    clearLabel={dict.common.clear}
                    options={requesterOptions}
                  />
                </div>
              )}
            </div>
          </motion.section>

          {summaryError && <div className="err">{summaryError}</div>}

          <motion.section variants={fadeUp} initial="hidden" animate="show">
            <FreeQuotaCard variant="full" />
          </motion.section>

          <motion.section
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="panel panel--hero usage-panel usage-panel--hero"
          >
            <div className="usage-hero-kicker-row">
              <p className="usage-hero-kicker">{dict.usage.totalSpend}</p>
              {canManageBilling && selectedMember && (
                <span className="chip usage-requester-badge">{dict.usage.requesterBadge(memberName(selectedMember))}</span>
              )}
            </div>
            <div className="balance">
              {summary ? formatTHB(summary.total.amount) : <PageLoader compact />}
            </div>
            <p className="note">{dict.usage.rangeNote(formatIsoDate(from), formatIsoDate(to), groupOptionLabel[groupBy])}</p>
          </motion.section>

          {canManageBilling && requesterFilter !== '' && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel usage-panel usage-panel--requester-models">
              <h2>{dict.usage.requesterModelsTitle(selectedMember ? memberName(selectedMember) : '')}</h2>
              {requesterSummary && requesterModelBuckets.length === 0 && (
                <EmptyState title={dict.usage.emptyTitle}>
                  <p className="note">{dict.usage.emptyBody}</p>
                </EmptyState>
              )}
              {requesterSummary && requesterModelBuckets.length > 0 && (
                <div className="table-scroll">
                  <table role="table" className="usage-table">
                    <thead role="rowgroup">
                      <tr role="row">
                        <th role="columnheader">{dict.usage.colModel}</th>
                        <th role="columnheader" className="num">{dict.usage.colRequests}</th>
                        <th role="columnheader" className="num">{dict.usage.colCost}</th>
                      </tr>
                    </thead>
                    <tbody role="rowgroup">
                      {requesterModelBuckets.map((b) => (
                        <tr role="row" key={b.label}>
                          <td role="cell" data-label={dict.usage.colModel}>{b.label}</td>
                          <td role="cell" className="num" data-label={dict.usage.colRequests}>{b.requests.toLocaleString()}</td>
                          <td role="cell" className="num" data-label={dict.usage.colCost}>{formatUsageCost(b.cost.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!requesterSummary && <PageLoader />}
            </motion.section>
          )}

          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel usage-panel usage-panel--breakdown">
            <h2>{dict.usage.breakdownBy(groupAxisLabel[groupBy])}</h2>
            {summary && summary.buckets.length === 0 && (
              <EmptyState title={dict.usage.emptyTitle}>
                <p className="note">{dict.usage.emptyBody}</p>
              </EmptyState>
            )}
            {summary && summary.buckets.length > 0 && (
              <>
                <BarList
                  items={orderedBuckets.slice(0, BAR_LIMIT).map((b) => ({
                    key: b.label,
                    label:
                      groupBy === 'day'
                        ? formatIsoDate(b.label)
                        : groupBy === 'key'
                          ? keyName(b, dict)
                          : b.label,
                    costMicro: toMicro(b.cost.amount),
                    valueLabel: `฿${formatUsageCost(b.cost.amount)}`,
                  }))}
                />
                {orderedBuckets.length > BAR_LIMIT && (
                  <p className="note">{dict.usage.showingTop(BAR_LIMIT, orderedBuckets.length)}</p>
                )}
                <p className="note usage-quota-footnote">{dict.usage.quotaFootnote}</p>

                <div className="table-scroll">
                  <table role="table" className="usage-table">
                    <thead role="rowgroup">
                      <tr role="row">
                        <th role="columnheader">{groupAxisLabel[groupBy]}</th>
                        <th role="columnheader" className="num">{dict.usage.colRequests}</th>
                        <th role="columnheader" className="num">{dict.usage.colSuccess}</th>
                        <th role="columnheader" className="num">{dict.usage.colFailed}</th>
                        <th role="columnheader" className="num">{dict.usage.colInputTok}</th>
                        <th role="columnheader" className="num">{dict.usage.colCacheReadTok}</th>
                        <th role="columnheader" className="num">{dict.usage.colOutputTok}</th>
                        <th role="columnheader" className="num">{dict.usage.colTotalTok}</th>
                        <th role="columnheader" className="num">{dict.usage.colCost}</th>
                      </tr>
                    </thead>
                    <tbody role="rowgroup">
                      {orderedBuckets.map((b) => (
                        <tr role="row" key={b.label}>
                          <td role="cell" data-label={groupAxisLabel[groupBy]}>
                            <span className="usage-bucket-label">
                              {groupBy === 'day' ? formatIsoDate(b.label) : groupBy === 'key' ? keyName(b, dict) : b.label}
                            </span>
                          </td>
                          <td role="cell" className="num" data-label={dict.usage.colRequests}>
                            {b.requests.toLocaleString()}
                          </td>
                          <td role="cell" className="num note-positive" data-label={dict.usage.colSuccess}>
                            {b.successful_requests.toLocaleString()}
                          </td>
                          <td role="cell" className="num note-warning" data-label={dict.usage.colFailed}>
                            {b.failed_requests.toLocaleString()}
                          </td>
                          <td role="cell" className="num" data-label={dict.usage.colInputTok}>
                            {b.input_tokens != null ? b.input_tokens.toLocaleString() : '—'}
                          </td>
                          <td role="cell" className="num" data-label={dict.usage.colCacheReadTok}>
                            {b.cache_read_tokens != null ? b.cache_read_tokens.toLocaleString() : '—'}
                          </td>
                          <td role="cell" className="num" data-label={dict.usage.colOutputTok}>
                            {b.output_tokens != null ? b.output_tokens.toLocaleString() : '—'}
                          </td>
                          <td role="cell" className="num usage-total-tok" data-label={dict.usage.colTotalTok}>
                            {b.input_tokens != null
                              ? ((b.input_tokens ?? 0) + (b.cache_read_tokens ?? 0) + (b.cache_write_tokens ?? 0) + (b.output_tokens ?? 0)).toLocaleString()
                              : '—'}
                          </td>
                          <td role="cell" className="num" data-label={dict.usage.colCost}>
                            {formatUsageCost(b.cost.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!summary && !summaryError && <PageLoader />}
          </motion.section>

          <div ref={eventsSectionRef} id="usage-events" className="usage-events-anchor">
            <motion.section
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="panel usage-panel usage-panel--events"
            >
            <h2>{dict.usage.allRequests}</h2>
            {eventsError && <div className="err">{eventsError}</div>}
            {!eventsLoadedOnce ? (
              <PageLoader />
            ) : (
            <div className="table-scroll">
              <table role="table" className="usage-table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{dict.usage.colWhen}</th>
                    {canManageBilling && <th role="columnheader">{dict.usage.colRequester}</th>}
                    <th role="columnheader">{dict.usage.colModel}</th>
                    <th role="columnheader">{dict.usage.colProvider}</th>
                    <th role="columnheader">{dict.usage.colStatus}</th>
                    <th role="columnheader" className="num">{dict.usage.colIn}</th>
                    <th role="columnheader" className="num">{dict.usage.colCacheReadTok}</th>
                    <th role="columnheader" className="num">{dict.usage.colOut}</th>
                    <th role="columnheader" className="num">{dict.usage.colCost}</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {events.length === 0 && (
                    <tr role="row">
                      <td role="cell" colSpan={canManageBilling ? 9 : 8}>
                        <EmptyState title={dict.usage.emptyRange} />
                      </td>
                    </tr>
                  )}
                  {events.map((e) => (
                    <tr role="row" key={e.event_id} className={e.status ?? ''}>
                      <td role="cell" data-label={dict.usage.colWhen}>
                        {formatDateTime(e.occurred_at)}
                      </td>
                      {canManageBilling && (
                        <td role="cell" data-label={dict.usage.colRequester}>
                          <span className="usage-requester-cell">
                            <span className="select-menu-item-avatar" aria-hidden="true">
                              {e.requester ? initials(requesterName(e, dict)) : '—'}
                            </span>
                            <span>{requesterName(e, dict)}</span>
                          </span>
                        </td>
                      )}
                      <td role="cell" data-label={dict.usage.colModel}>
                        <span className="usage-model-cell">
                          <ModelVendorMark modelId={e.model_id} size={16} />
                          <span className="usage-model">{e.model_id}</span>
                          {e.promo_applied && (
                            <span
                              className="badge badge--model-free usage-free-badge"
                              title={dict.usage.freeBadgeTitle}
                              aria-label={dict.usage.freeBadgeTitle}
                            >
                              {dict.usage.freeBadge}
                            </span>
                          )}
                        </span>
                      </td>
                      <td role="cell" data-label={dict.usage.colProvider}>
                        {e.provider}
                      </td>
                      <td role="cell" data-label={dict.usage.colStatus}>
                        <span className={`badge badge--${e.status ?? 'completed'}`}>
                          {eventStatusLabel(e.status, dict)}
                        </span>
                      </td>
                      <td role="cell" className="num" data-label={dict.usage.colIn}>
                        {e.tokens.input.toLocaleString()}
                      </td>
                      <td role="cell" className="num" data-label={dict.usage.colCacheReadTok}>
                        {e.tokens.cache_read > 0 ? e.tokens.cache_read.toLocaleString() : '—'}
                      </td>
                      <td role="cell" className="num" data-label={dict.usage.colOut}>
                        {e.tokens.output.toLocaleString()}
                      </td>
                      <td role="cell" className="num" data-label={dict.usage.colCost}>
                        <span className="usage-cost-cell">
                          <span>{formatUsageCost(e.cost.amount)}</span>
                          {e.promo_applied && e.list_cost && (
                            <span className="usage-cost-saved">
                              {dict.usage.savedCost(formatUsageCost(e.list_cost.amount))}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <CursorPagePager
              page={eventsPage}
              loaded={events.length}
              pageIndex={pageIndex + 1}
              busy={eventsLoading}
              onPrev={goPrev}
              onNext={goNext}
              pageSize={eventPageSize}
              pageSizeOptions={[...EVENT_PAGE_SIZES]}
              onPageSizeChange={changePageSize}
            />
          </motion.section>
          </div>
        </>
      )}
    </div>
  );
}
